/**
 * Rounding helpers shared by the grade tools.
 *
 * Grades sit on boundaries — 89.5, 69.5, 2.25 — and binary floating point lands a hair
 * either side of them: `0.575 * 100` is 57.49999999999999, which `Math.round` sends the
 * wrong way. `cleanFloat` snaps a value to nine decimal places first, which is far finer
 * than any grade scale and far coarser than the floating-point noise.
 */

export type RoundingMode = "nearest" | "down" | "up";

/** Remove floating-point noise below the ninth decimal place. */
export function cleanFloat(value: number): number {
  return Math.round(value * 1e9) / 1e9;
}

/**
 * Round to `decimals` places. `nearest` sends an exact half up (89.5 → 90), which is the
 * convention grade scales and the ACT both use for positive numbers.
 */
export function roundTo(
  value: number,
  decimals: number,
  mode: RoundingMode = "nearest"
): number {
  const factor = 10 ** decimals;
  const scaled = cleanFloat(value * factor);
  if (mode === "down") {
    return Math.floor(scaled) / factor;
  }
  if (mode === "up") {
    return Math.ceil(scaled) / factor;
  }
  return Math.floor(scaled + 0.5) / factor;
}

/** Cut to `decimals` places without rounding (2.29 → 2.2), as German grade rules do. */
export function truncateTo(value: number, decimals: number): number {
  return roundTo(value, decimals, "down");
}
